/**
 * EntityGroup — semantic entity section within the field picker.
 *
 * Shows an entity header (icon + label + divider), then optionally
 * sub-groups fields by role (Measures / Dimensions / Dates) if the
 * entity contains mixed field types. Small entities with a single
 * role type are shown as a flat list without sub-headers.
 */
import { useMemo } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import type { DatasetViewColumn } from "../../../../api/types";
import FieldTypeSection from "./FieldTypeSection";
import FieldListItem from "./FieldListItem";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    marginBottom: "4px",
  },
  headerWrapper: {
    position: "sticky" as const,
    top: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px 4px",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    letterSpacing: "0.01em",
  },
  divider: {
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
    margin: "0 12px 2px",
  },
});

// ─── Role classification ─────────────────────────────────────────────────────

type RoleGroup = "Measures" | "Dimensions" | "Dates" | "IDs" | "Other";

function classifyRole(col: DatasetViewColumn): RoleGroup {
  if (col.semantic_type === "time") return "Dates";
  if (col.semantic_type === "id" || col.semantic_type === "identifier") return "IDs";
  if (col.semantic_type === "measure") return "Measures";
  if (col.default_aggregation && col.default_aggregation !== "none") return "Measures";
  if (col.semantic_type === "dimension" || col.semantic_type === "category") return "Dimensions";
  return "Other";
}

function sortByLabel(cols: DatasetViewColumn[]): DatasetViewColumn[] {
  return [...cols].sort((a, b) => {
    const la = (a.display_name || a.name).toLowerCase();
    const lb = (b.display_name || b.name).toLowerCase();
    return la.localeCompare(lb);
  });
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface EntityGroupProps {
  label: string;
  fields: DatasetViewColumn[];
  selectedValue?: string;
  onSelect: (colName: string) => void;
  hideHeader?: boolean;
}

export default function EntityGroup({
  label,
  fields,
  selectedValue,
  onSelect,
  hideHeader,
}: EntityGroupProps) {
  const styles = useStyles();

  // Group by role
  const roleGroups = useMemo(() => {
    const map = new Map<RoleGroup, DatasetViewColumn[]>();
    for (const f of fields) {
      const role = classifyRole(f);
      if (!map.has(role)) map.set(role, []);
      map.get(role)!.push(f);
    }
    return map;
  }, [fields]);

  // Only show sub-sections if entity has multiple role types
  const hasMixedTypes = roleGroups.size > 1;
  const roleOrder: RoleGroup[] = ["Measures", "Dimensions", "Dates", "IDs", "Other"];

  return (
    <div className={styles.root}>
      {!hideHeader && (
        <div className={styles.headerWrapper}>
          <div className={styles.header}>
            <span>{label}</span>
          </div>
          <div className={styles.divider} />
        </div>
      )}

      {hasMixedTypes
        ? roleOrder.map((role) => {
            const cols = roleGroups.get(role);
            if (!cols || cols.length === 0) return null;
            const sorted = sortByLabel(cols);
            return (
              <FieldTypeSection key={role} label={role}>
                {sorted.map((col) => (
                  <FieldListItem
                    key={col.name}
                    column={col}
                    isSelected={col.name === selectedValue}
                    onSelect={onSelect}
                  />
                ))}
              </FieldTypeSection>
            );
          })
        : sortByLabel(fields).map((col) => (
            <FieldListItem
              key={col.name}
              column={col}
              isSelected={col.name === selectedValue}
              onSelect={onSelect}
            />
          ))}
    </div>
  );
}
